import { EquityItem } from '@app/modules/projects/inputs/capital-structure/CapitalStructureItemFormErf';
import {
  CapitalStructureBuildStructureDto,
  CapitalStructureEventDto,
  CapitalStructureEventSetDto,
  CapitalStructureInstrumentDefinitionDto,
} from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';
import {
  InstrumentType,
  OwnerType,
  EventKey,
} from '@app/shared/models/contracts/enums/shared-enums';
import { createSelector } from '@reduxjs/toolkit';
import { enumKeyByValue } from '@app/shared/helpers';
import {
  EquityRefinancingEventDto,
  InstrumentDefinitionDto,
  InstrumentInitialValuesDto,
} from '@app/shared/models/contracts/project-dto';
import { RootState } from './store';
import {
  createInstrumentFilterSelector,
  instrumentFilters,
} from './factories/instrument-selector-factory';
import { getEventSetId } from '@app/modules/projects/inputs/capital-structure/utils/CapitalStructureHelper';
import { addEligibleInstrumentsToAllDividendSets } from '../inputs/addEligibleInstrumentsToAllDividendSets';

export type InstrumentDefinitionWithId = InstrumentDefinitionDto & { instrumentId: string };
export type InstrumentDefinitionAndInitialValues = InstrumentDefinitionWithId & {
  initialValues: Nullable<InstrumentInitialValuesDto>;
};

const selectProjectDraft = (state: RootState) => state.project.projectDraft;
const selectCapitalStructureState = (state: RootState) => state.capitalStructure.values;

const createOwnershipMapping = (ownershipAfterMovements: any) =>
  Object.keys(OwnerType).map((owner) => ({
    owner: owner as keyof typeof OwnerType,
    amount: ownershipAfterMovements?.[owner]?.amount ?? null,
    numberOfShares: ownershipAfterMovements?.[owner]?.numberOfShares ?? null,
  }));

const transformInstrumentWithOwnership = (
  instrument: any,
  instrumentDefinitions: Dictionary<CapitalStructureInstrumentDefinitionDto>
) => ({
  ...instrument,
  ...instrumentDefinitions[instrument.instrumentId],
  ownership: createOwnershipMapping(instrument.ownershipAfterMovements),
});

const extractInstrumentsFromEvents = (
  events: CapitalStructureEventDto[],
  instrumentDefinitions: Dictionary<CapitalStructureInstrumentDefinitionDto>
): EquityItem[] => {
  const instruments = events.flatMap(
    (event) =>
      event.ranks?.flatMap((rank) =>
        Object.entries(rank.instruments || {}).map(([instrumentId, instrument]) => ({
          ...instrument,
          instrumentId,
        }))
      ) ?? []
  );

  return instruments.map((instrument) =>
    transformInstrumentWithOwnership(instrument, instrumentDefinitions)
  );
};

export const selectCapitalStructures = createSelector(
  [selectProjectDraft],
  (projectDraft) => projectDraft.capitalStructures
);

export const selectBuildStructuresResults = createSelector(
  [selectCapitalStructureState],
  (capitalStructureResults) => capitalStructureResults.buildStructures
);

export const selectBuildStructureResults = createSelector(
  [selectCapitalStructureState],
  (capitalStructureResults) => Object.values(capitalStructureResults.buildStructures)[0] || {}
);

export const selectCapitalStructure = createSelector(
  [selectCapitalStructures],
  (capitalStructures) => Object.values(capitalStructures)[0] || {}
);

export const selectCapitalStructureId = createSelector(
  [selectCapitalStructures],
  (capitalStructures) => Object.keys(capitalStructures)[0] || ''
);

export const selectInstruments = createSelector(
  [selectCapitalStructure],
  (capitalStructure): InstrumentDefinitionWithId[] => {
    if (!capitalStructure.instrumentDefinitions) {
      return [];
    }
    return Object.entries(capitalStructure.instrumentDefinitions).map(
      ([instrumentId, instrument]) => ({
        instrumentId,
        ...instrument,
      })
    );
  }
);

export const selectOrdinaryEquityInstruments = createInstrumentFilterSelector(
  instrumentFilters.byType(InstrumentType.OrdinaryEquity)
);

export const selectPrefShareInstruments = createInstrumentFilterSelector(
  instrumentFilters.byType(InstrumentType.PreferredShares)
);

export const selectSlnInstruments = createInstrumentFilterSelector(
  instrumentFilters.byType(InstrumentType.ShareholderLoanNotes)
);

export const selectSlnAndPrefSharesInstruments = createInstrumentFilterSelector(
  instrumentFilters.excludeType(InstrumentType.OrdinaryEquity)
);

export const selectValuedInstruments = createInstrumentFilterSelector(
  instrumentFilters.shouldBeValued
);

export const selectNonOpmOnlyEventsByEventSetId = (eventSetId: string) =>
  createSelector([selectCapitalStructure], (capitalStructure): EquityRefinancingEventDto[] => {
    if (!capitalStructure.eventSets || !capitalStructure.events) {
      return [];
    }

    const opmOnlyEventIds = capitalStructure.eventSets[EventKey.OpmOnly]?.events || [];
    const caseEventIds = capitalStructure.eventSets[eventSetId]?.events || [];
    const nonOpmOnlyEvents = Object.entries(capitalStructure.events)
      .filter(([eventId]) => !opmOnlyEventIds.includes(eventId) && caseEventIds.includes(eventId))
      .map(([_, event]) => event);

    return nonOpmOnlyEvents;
  });

/**
 * Selects instruments and transforms them into an object where each key is the instrument ID,
 * and the value is the instrument's details.
 * @returns An object where keys are instrument IDs and values are the instrument details.
 */
export const selectInstrumentDefinitions = createSelector([selectInstruments], (instruments) => {
  return instruments.reduce((acc, instrument) => {
    const { instrumentId, ...details } = instrument;
    acc[instrumentId] = details;
    return acc;
  }, {} as Record<string, Omit<InstrumentDefinitionWithId, 'instrumentId'>>);
});

/**
 * Selects dividend data with all eligible instruments normalised across all dividend sets.
 * Ensures that any instrument marked as dividendEligible appears in all dividend sets
 * with appropriate placeholder values for dates.
 * @returns Normalised dividend data with historicDividendInstruments, forecastDividendSets, and instrumentDefinitions
 */
export const selectNormalisedDividendData = createSelector(
  [
    (state: RootState) => state.project.projectDraft.capitalStructures,
    selectInstrumentDefinitions,
    selectProjectDraft,
    (_state: RootState, historicDividendYears: string[]) => historicDividendYears,
  ],
  (capitalStructures, instrumentDefinitions, projectDraft, historicDividendYears) => {
    const capitalStructure = Object.values(capitalStructures)[0] || {};

    const { updatedForecastSets, updatedHistoricInstruments } =
      addEligibleInstrumentsToAllDividendSets(
        capitalStructure.historicDividendInstruments || {},
        capitalStructure.forecastDividendSets || {},
        instrumentDefinitions,
        projectDraft,
        historicDividendYears
      );

    return {
      historicDividendInstruments: updatedHistoricInstruments,
      forecastDividendSets: updatedForecastSets,
      instrumentDefinitions,
    };
  }
);

/**
 * Selects instruments with their initial values from the capital structure.
 * @returns An array of `InstrumentDefinitionAndInitialValues` objects.
 *          The `initialValues` property of each object may be `null` if the instrument
 *          was not added in the initial capital structure.
 */
export const selectInstrumentsWithInitialValues = createSelector(
  [selectCapitalStructure, selectInstruments],
  (capitalStructure, instrumentDefinitions): InstrumentDefinitionAndInitialValues[] => {
    return instrumentDefinitions
      .filter((instrument) => instrument.instrumentId in capitalStructure.initialValues)
      .map((instrument) => ({
        ...instrument,
        initialValues: capitalStructure.initialValues[instrument.instrumentId],
      }));
  }
);

/**
 * Selects all instruments and includes their initial values if they exist on the original structure.
 * @returns An array of `InstrumentDefinitionAndInitialValues` objects.
 *          The `initialValues` property of each object may be `null` if the instrument
 *          was not added in the initial capital structure.
 */
export const selectAllInstrumentsIncludingInitialValues = createSelector(
  [selectCapitalStructure, selectInstruments],
  (capitalStructure, instrumentDefinitions): InstrumentDefinitionAndInitialValues[] => {
    return instrumentDefinitions.map((instrument) => ({
      ...instrument,
      initialValues: capitalStructure.initialValues[instrument.instrumentId] ?? null,
    }));
  }
);

/**
 * Selects all debt instruments and includes their initial values from the capital structure if present.
 * @returns An array of `InstrumentDefinitionAndInitialValues` objects.
 *          The `initialValues` property of each object may be `null` if the instrument
 *          was not added in the initial capital structure.
 */
export const selectSlnAndPrefSharesInstrumentsIncludeInitialValues = createSelector(
  [selectCapitalStructure, selectInstruments],
  (capitalStructure, instrumentDefinitions): InstrumentDefinitionAndInitialValues[] => {
    return instrumentDefinitions
      .filter((ins) => ins.type !== enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity))
      .map((instrument) => ({
        ...instrument,
        initialValues: capitalStructure.initialValues[instrument.instrumentId] ?? {
          ownership: {},
          coupon: null,
        },
      }));
  }
);

/**
 * Selects debt instruments with their initial values from the capital structure.
 * @returns An array of `InstrumentDefinitionAndInitialValues` objects.
 *          The `initialValues` property of each object may be `null` if the instrument
 *          was not added in the initial capital structure.
 */
export const selectSlnAndPrefSharesInstrumentsWithInitialValues = createSelector(
  [selectInstrumentsWithInitialValues, selectSlnAndPrefSharesInstruments],
  (allInstruments, slnAndPrefSharesInstruments): InstrumentDefinitionAndInitialValues[] => {
    const slnAndPrefSharesInstrumentIds = new Set(
      slnAndPrefSharesInstruments.map((instrument) => instrument.instrumentId)
    );

    return allInstruments.filter((instrument) =>
      slnAndPrefSharesInstrumentIds.has(instrument.instrumentId)
    );
  }
);

/**
 * Selects instruments with their initial values and maps them to a record of instrument IDs.
 * @returns A record where each key is an instrument ID, and the value is an object containing:
 *          - `ownership`: The ownership details of the instrument (defaulting to an empty object if not provided).
 *          - `coupon`: The coupon value of the instrument (defaulting to `null` if not provided).
 */
export const selectInstrumentsWithInitialValuesInitialInputs = createSelector(
  [selectInstrumentsWithInitialValues],
  (instrumentsWithInitialValues) => {
    return instrumentsWithInitialValues.reduce((acc, instrument) => {
      acc[instrument.instrumentId] = {
        ownership: instrument.initialValues?.ownership ?? {},
        coupon: instrument.initialValues?.coupon ?? null,
      };
      return acc;
    }, {} as Record<string, { ownership: any; coupon: any | null }>);
  }
);

/**
 * Selects instrument IDs that are present in events for a specific dividend set,
 * falling back to instruments with initial values if no events exist
 * @param dividendSetId - The dividend set ID to filter by
 * @returns A Set of instrument IDs that are relevant for the dividend set
 */
export const selectInstrumentIdsByDividendSet = (dividendSetId: string | undefined) =>
  createSelector(
    [selectProjectDraft, selectCapitalStructureId, selectBuildStructuresResults],
    (projectDraft, capitalStructureId, buildStructures): Set<string> => {
      if (!dividendSetId || !capitalStructureId) {
        return new Set<string>();
      }

      const matchingCase = projectDraft.pwermInput.cases.find(
        (c) => c.dividendSetId === dividendSetId
      );

      const capitalStructure = buildStructures[capitalStructureId];

      const eventSetId = getEventSetId(
        projectDraft.details.calcMethod,
        Object.keys(capitalStructure.eventSets),
        matchingCase
      );
      const instrumentDefinitions = capitalStructure?.instrumentDefinitions || {};

      const instrumentIds = Object.entries(instrumentDefinitions)
        .filter(([_, instrument]) => {
          return (instrument.linkedEventSetIds ?? []).includes(eventSetId);
        })
        .map(([id]) => id);

      return new Set(instrumentIds);
    }
  );

export const getInitialTotalEquityByInstrumentTypeAndOwnership = (
  instruments: InstrumentDefinitionAndInitialValues[],
  instrumentTypeFilter?: keyof typeof InstrumentType,
  ownershipFilter?: keyof typeof OwnerType
): number | null => {
  let amount: null | number = null;
  const filteredInstruments = instrumentTypeFilter
    ? instruments.filter((ins) => ins.type === instrumentTypeFilter)
    : instruments;

  filteredInstruments.map((ins) => {
    const ownership = ins.initialValues?.ownership;

    if (ownership) {
      Object.entries(ownership).map(([ownerType, ownershipItem]) => {
        if (!ownershipFilter || ownerType === ownershipFilter) {
          if (ownershipItem.amount) {
            const currentValue = Number(ownershipItem.amount);
            amount ? (amount += currentValue) : (amount = currentValue);
          }
        }
      });
    }
  });

  return amount;
};

export const getAllInstrumentsFromBuildStructure = (
  buildStructure: Dictionary<CapitalStructureBuildStructureDto>
): EquityItem[] => {
  const buildStructureValues = Object.values(buildStructure);

  if (buildStructureValues.length === 0) {
    return [];
  }

  const firstStructure = buildStructureValues[0];
  if (!firstStructure?.instrumentDefinitions || !firstStructure?.eventSets) {
    return [];
  }

  const { instrumentDefinitions, eventSets } = firstStructure;
  const allEvents = Object.values(eventSets).flatMap((eventSet) => eventSet.events);

  return extractInstrumentsFromEvents(allEvents, instrumentDefinitions);
};

export const getEventInstrumentsFromBuildStructure = (
  buildStructure: Dictionary<CapitalStructureBuildStructureDto>,
  eventSetId: string,
  eventId: string
): EquityItem[] => {
  const eventSet = Object.values(buildStructure)[0].eventSets[eventSetId];
  const instrumentDefinitions = Object.values(buildStructure)[0].instrumentDefinitions;
  const event = eventSet.events.find((event) => event.id === eventId);

  if (!event) {
    return [];
  }

  return extractInstrumentsFromEvents([event], instrumentDefinitions);
};

export const getEventInstrumentsFromEvent = (
  event: CapitalStructureEventDto,
  instrumentDefinitions: Dictionary<CapitalStructureInstrumentDefinitionDto>
): EquityItem[] => {
  return extractInstrumentsFromEvents([event], instrumentDefinitions);
};

export const getDuplicateGroups = (eventSets: [string, CapitalStructureEventSetDto][]) => {
  const processedEventSetIds = new Set<string>();
  const groups: string[][] = [];

  const eventSetMap = Object.fromEntries(eventSets);

  const traverseEventSet = (eventSetId: string, currentGroup: string[]) => {
    processedEventSetIds.add(eventSetId);
    currentGroup.push(eventSetId);

    for (const duplicateId of eventSetMap[eventSetId].duplicates) {
      if (!processedEventSetIds.has(duplicateId)) {
        traverseEventSet(duplicateId, currentGroup);
      }
    }
  };

  for (const [eventSetId] of eventSets) {
    if (!processedEventSetIds.has(eventSetId)) {
      const currentGroup: string[] = [];
      traverseEventSet(eventSetId, currentGroup);
      groups.push(currentGroup);
    }
  }

  return groups;
};
